import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const UserAuthContext = createContext(null);

const config = {
  backendBaseUrl: process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080',
  accessTokenKey: 'neopass_user_access_token',
  refreshTokenKey: 'neopass_user_refresh_token',
};

export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (!context) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
};

export const UserAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(() => 
    localStorage.getItem(config.accessTokenKey)
  );
  const [refreshToken, setRefreshToken] = useState(() => 
    localStorage.getItem(config.refreshTokenKey)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const storeTokens = useCallback((access, refresh) => {
    setAccessToken(access);
    setRefreshToken(refresh);
    if (access) {
      localStorage.setItem(config.accessTokenKey, access);
    } else {
      localStorage.removeItem(config.accessTokenKey);
    }
    if (refresh) {
      localStorage.setItem(config.refreshTokenKey, refresh);
    } else {
      localStorage.removeItem(config.refreshTokenKey);
    }
  }, []);

  const clearAuth = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem(config.accessTokenKey);
    localStorage.removeItem(config.refreshTokenKey);
  }, []);

  const login = useCallback(async (username, password) => {
    setError(null);
    setIsLoading(true);
    const normalizedUsername = username.toLowerCase().trim(); // Normalize username
    
    try {
      const response = await fetch(`${config.backendBaseUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: normalizedUsername, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Login failed');
      }

      storeTokens(data.accessToken, data.refreshToken);
      setUser(data.account);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [storeTokens]);

  const signup = useCallback(async (username, password, email) => {
    setError(null);
    setIsLoading(true);
    const normalizedUsername = username.toLowerCase().trim(); // Normalize username
    
    try {
      const response = await fetch(`${config.backendBaseUrl}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: normalizedUsername, password, email: email?.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Signup failed');
      }

      storeTokens(data.accessToken, data.refreshToken);
      setUser(data.account);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [storeTokens]);

  const setEmail = useCallback(async (email) => {
    if (!accessToken) {
      return { success: false, error: 'Not authenticated' };
    }

    setError(null);
    try {
      const response = await fetch(`${config.backendBaseUrl}/api/account/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ email: email?.trim() }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to set email');
      }

      if (data?.account) {
        setUser(data.account);
      }

      return { success: true, account: data.account };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, [accessToken]);

  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) {
      clearAuth();
      return null;
    }

    try {
      const response = await fetch(`${config.backendBaseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        clearAuth();
        return null;
      }

      storeTokens(data.accessToken, data.refreshToken);
      setUser(data.account);
      return data.accessToken;
    } catch (err) {
      clearAuth();
      return null;
    }
  }, [refreshToken, storeTokens, clearAuth]);

  const logout = useCallback(async () => {
    try {
      if (refreshToken) {
        await fetch(`${config.backendBaseUrl}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch (err) {
      // Ignore logout errors
    } finally {
      clearAuth();
    }
  }, [refreshToken, clearAuth]);

  const fetchAccount = useCallback(async () => {
    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${config.backendBaseUrl}/api/account`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          const retryResponse = await fetch(`${config.backendBaseUrl}/api/account`, {
            headers: { 'Authorization': `Bearer ${newToken}` },
          });
          const data = await retryResponse.json();
          if (retryResponse.ok) {
            setUser(data.account);
          }
        }
        return;
      }

      const data = await response.json();
      if (response.ok) {
        setUser(data.account);
      } else {
        clearAuth();
      }
    } catch (err) {
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, refreshAccessToken, clearAuth]);

  useEffect(() => {
    fetchAccount();
  }, []);

  const getAccessToken = useCallback(async () => {
    if (!accessToken) return null;
    return accessToken;
  }, [accessToken]);

  const value = {
    user,
    accessToken,
    isAuthenticated: !!user && !!accessToken,
    isLoading,
    error,
    login,
    signup,
    setEmail,
    logout,
    refreshAccessToken,
    getAccessToken,
    fetchAccount,
    backendBaseUrl: config.backendBaseUrl,
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
};

export default UserAuthContext;
